import * as React from 'react';
import { useEffect, useState } from 'react';
import 'react-dropdown-tree-select/dist/styles.css';
import "react-widgets/styles.css";
import { Card, CardBody, CardText, CardTitle, Col, Input, Label, Row, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { AffiliateReporting, Afr, Audit, getFirstDocumentIndexFromRole, getSelectedAfr, saveDocumentRelationship, setAuditReceivedDate } from '../../redux/AfrSlice';
import { DocumentRelationship, downloadDocumentById, uploadFile } from '../../redux/DocumentsSlice';
import { setIsDirty } from '../../redux/MiscSlice';
import dateHelper from '../../utils/dateHelper';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { GenericFileUploader } from '../core/GenericFileUploader';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';

const UploadDocumentByRole = (props:
    {
        role: string,        /* source: enums.documentRole */
        pdfType: string,               /* source: enums.pdfType */
        containerParentOfAudit: any,   /* it can be an instance of Affiliated Entity or the AFR current Version */
        showCheckbox: boolean,
        saveAudit: (audit: Audit) => void
    }) => {

    //TODO create a generic reporter component to actually render the topic and details.
    //props should include title and detail list
    const dispatch = useAppDispatch();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);

    const [showWaivers, setShowWeivers] = useState(false);
    const [waiversFlag, setWaiversFlag] = useState(false);
    const [file, setFile] = useState<File>();
    const [hasAudit, setHasAudit] = useState(false);

    const selectDocumentByRole = function (afr: Afr, affiliate : string | null): DocumentRelationship | null {
        var matchDoc: DocumentRelationship | null = null;
         for (var doc of afr.currentversion.documents) {
            if (doc.role !== props.role) {
                continue;
            }
            if (affiliate) {
                if (doc.affiliate === affiliate) {
                    matchDoc = doc;
                }
            } else {
                matchDoc = doc;
            }
        }
       return matchDoc;
    }

    const hasDeclaredIntentionToNotUpload = function (afr: Afr | null): boolean {
        if (!afr) {
            return false;
        }
        if (props.role === enums.documentRole.audit) {
            // Audit documents are not stored in the general version documents list, but are attached to the relevant audit object
            if (props.containerParentOfAudit.audit) {
                var audit = JSON.parse(JSON.stringify(props.containerParentOfAudit.audit));
                if (audit) {
                    return audit.sendAuditPdf;
                }
            }
            return false;
        }
        if (!afr.currentversion.documents || afr.currentversion.documents.length === 0) {
            return false;
        }
        var existingDoc: DocumentRelationship | null = null;
        switch (props.pdfType) {
            case enums.pdfType.AFRAudit:
            case enums.pdfType.AffiliatedEntityAudit:
                if (props.containerParentOfAudit && props.containerParentOfAudit.audit) {
                    return props.containerParentOfAudit.audit.sendAuditPdf;
                } else {
                    return false;
                }

            case enums.pdfType.AFRExceptions:
            case enums.pdfType.AFRWaivers:
            case enums.pdfType.AFRAffidavit:
                existingDoc = selectDocumentByRole(afr, null);
                break;
            case enums.pdfType.AEBudgetVarianceReport:
                existingDoc = selectDocumentByRole(afr, props.containerParentOfAudit ? props.containerParentOfAudit.affiliateReference.code : null );
                break;
        }
        var result = false;
        if (existingDoc) {
            result = utilityHelper.isNullDocumentRelationship(existingDoc);
        }
        return result;
    }


    const [checkboxPdf, setCheckboxPdf] = useState(hasDeclaredIntentionToNotUpload(currentAfr)); /* Used for SendAuditPDF or No Exceptions and Weivers */
    const [deferAuditPDF, setDeferAuditPDF] = useState(hasDeclaredIntentionToNotUpload(currentAfr));
    const [auditDateFormatted, setAuditDateFormatted] = useState("");
    const [audReceivedDate, setAudReceivedDate] = useState("");
    const [pdfDocumentId, setPdfDocumentId] = useState("");
    const [fileName, setFileName] = useState("");
    const [pdfUploadedDate, setPdfUploadedDate] = useState("");

    var settings = {
        upload: { legend: "", entityType: "", actionType: "UPLOAD_PDF", checkboxLegend:"", cardTitle:"Audit PDF", successfulUploadMessage:"" },
    }

    switch (props.pdfType) {
        case enums.pdfType.AffiliatedEntityAudit:
            settings.upload.legend = "Would you like to upload a PDF version of your CRA’s audit?";
            settings.upload.checkboxLegend = "I will send you my Audit PDF";
            settings.upload.entityType = "AFFILIATED_ENTITY";
            settings.upload.successfulUploadMessage = "Audit Uploaded Successfully!"
            break;
        case enums.pdfType.AFRAudit:
            settings.upload.legend = "Would you like to upload a PDF version of your audit?";
            settings.upload.checkboxLegend = "I will send you my Audit PDF";
            settings.upload.entityType = "AUDIT";
            settings.upload.successfulUploadMessage = "Audit Uploaded Successfully!"
            if (pdfDocumentId && pdfDocumentId.length > 0) {
                settings.upload.legend += "|A replacement audit document should only be uploaded if there have been revisions to the document since the initial upload."
            }
            break;
        case enums.pdfType.AFRExceptions:
            settings.upload.legend = "Would you like to upload a PDF version of your Impact Fee Exceptions?";
            settings.upload.checkboxLegend = "I have no Impact Fee Exceptions";
            settings.upload.entityType = "EXCEPTIONS";
            settings.upload.cardTitle = "Exceptions PDF";
            settings.upload.successfulUploadMessage = "Exceptions Uploaded Successfully!"
            break;
        case enums.pdfType.AFRWaivers:
            settings.upload.legend = "Would you like to upload a PDF version of your Impact Fee Waivers?";
            settings.upload.checkboxLegend = "I have no Impact Fee Waivers";
            settings.upload.entityType = "WAIVERS";
            settings.upload.cardTitle = "Waivers PDF";
            settings.upload.successfulUploadMessage = "Waivers Uploaded Successfully!"
            break;
        case enums.pdfType.AFRAffidavit:
            settings.upload.legend = "Upload your signed affidavit.";
            settings.upload.checkboxLegend = "I have no signed affidavit";
            settings.upload.entityType = "AFFIDAVIT";
            settings.upload.cardTitle = "Affidavit PDF";
            settings.upload.successfulUploadMessage = "Affidavit Uploaded Successfully!"
            break;
        case enums.pdfType.AEBudgetVarianceReport:
            settings.upload.legend = "Upload your Budget Variance Report.";
            settings.upload.checkboxLegend = "No applicable budget variance report";
            settings.upload.entityType = "BUDGET_VARIANCE_REPORT";
            settings.upload.cardTitle = "Budget Variance Report PDF";
            settings.upload.successfulUploadMessage = "Budget Variance Report Uploaded Successfully!"
            break;
    }
    

    const getArrayDocuments = function () {
        if (currentAfr && currentAfr.currentversion && currentAfr.currentversion.documents && currentAfr.currentversion.documents.length > 0) {
            return currentAfr.currentversion.documents;
        }
        return null;
    }

    const showUploadSuccessful = function () {
        Swal.fire(utilityHelper.uploadSuccessfulNotFinished);
    }

    // action to upload and process the file
    const handleUploadFile = async function (actionType: string, entityType: string, entityId: string, uploadedFile: File | undefined) {
        if (uploadedFile !== undefined) {
            var audit: Audit | null = null;
            let document: DocumentRelationship;
            var now = new Date().toISOString();
            var documentGuid = "";

            if (props.role === enums.documentRole.audit) {

                // Upload file and set back the document id
                documentGuid = await uploadFile(uploadedFile, "Audit PDF document", dispatch);

                if (props.containerParentOfAudit.audit) {
                    audit = JSON.parse(JSON.stringify(props.containerParentOfAudit.audit));
                    if (audit) {
                        audit.auditPdf = { documentId: documentGuid, role: "AUDIT", timestamp: now, name: uploadedFile.name, affiliate: null }
                    }
                } else {
                    audit = { auditorReference: null, auditPdf: { documentId: documentGuid, role: "AUDIT", timestamp: now, name: uploadedFile.name, affiliate: null }, auditType: "", datePerformed: "", sendAuditPdf: null, dateReceived: "" }
                }

            } else {
                // Upload file and set back the document id
                documentGuid = await uploadFile(uploadedFile, `${settings.upload.entityType} PDF document`, dispatch);

                // Adding/Replacing the file into AFRVersion.documents: Array<DocumentRelationship>;
                // code here
                if (props.containerParentOfAudit) {
                    var ar: AffiliateReporting = props.containerParentOfAudit as AffiliateReporting;
                    if (ar && ar.affiliateReference) {
                        document = { documentId: documentGuid, role: props.role, timestamp: now, name: uploadedFile.name, affiliate: ar.affiliateReference.code }
                    } else {
                        document = { documentId: documentGuid, role: props.role, timestamp: now, name: uploadedFile.name, affiliate: null }
                    }
                } else {
                    document = { documentId: documentGuid, role: props.role, timestamp: now, name: uploadedFile.name, affiliate: null }
                }
                dispatch(saveDocumentRelationship(document));
            }

            // Turn ON the dirty flag in redux
            dispatch(setIsDirty({ IsDirty: true, Page: "" }));

            if (props.role === enums.documentRole.audit && audit) {
                //send back to the caller to save each change to the audit object
                props.saveAudit(audit);
            }
            setPdfDocumentId(documentGuid);
            setFileName(uploadedFile.name);

            if (documentGuid.length > 0) {
                showUploadSuccessful();
            }
        }    
    }
     
      

    const handleChangeCheckbox = function (event:any) {

        var value: boolean = event.target.checked;
        let document: DocumentRelationship;
        var audit: Audit | null = null;
        var now = new Date().toISOString();
        if (props.role === enums.documentRole.audit) {

            if (props.containerParentOfAudit.audit) {
                audit = JSON.parse(JSON.stringify(props.containerParentOfAudit.audit));
                if (audit) {
                    if (value === false) {
                        if (audit.sendAuditPdf === true) {
                            // We are switching to false, so remove the send document from our previous state
                            audit.auditPdf = null;
                        }
                    }
                    audit.sendAuditPdf = value;
                }
            } else {
                audit = { auditorReference: null, auditPdf: null, auditType: "", datePerformed: "", sendAuditPdf: value, dateReceived: "" }
            }
            if (value && audit) {
                audit.auditPdf = null;
            }
        } else {

            // Update the document collection
            if (props.containerParentOfAudit) {
                var ar: AffiliateReporting = props.containerParentOfAudit as AffiliateReporting;
                if (ar && ar.affiliateReference) {
                    document = { documentId: null, role: props.role, timestamp: now, name: "", affiliate: ar.affiliateReference.code }
                } else {
                    document = { documentId: null, role: props.role, timestamp: now, name: "", affiliate: null }
                }
            } else {
                document = { documentId: null, role: props.role, timestamp: now, name: "", affiliate: null }
            }
            dispatch(saveDocumentRelationship(document));

        }

        // Turn ON the dirty flag in redux
        dispatch(setIsDirty({ IsDirty: true, Page: "" }));

        setCheckboxPdf(value);
        setDeferAuditPDF(value);

        if ((props.role === enums.documentRole.audit) && audit) {
            //send back to the caller to save each change to the audit object
            props.saveAudit(audit);
        }

    }

    const showPdfInformation = function () {

        if (props.role === enums.documentRole.audit) {
            return cardAuditInformation();
        }
        else {
            return cardWaiverInformation();
        }

    }

    const handleClickDownload = (docId: string | null) => async (event: any) => {
        if (docId) {
            await dispatch(downloadDocumentById(docId));
        }
    }

    const cardAuditInformation = function () {

        var title = `${settings.upload.entityType}`;
        var text =
            <Card body>
                <CardTitle>{title}</CardTitle>
                {
                    (pdfDocumentId.length > 0)
                        ?
                        <CardBody>
                            {(deferAuditPDF)
                                ?
                                <ul>
                                    <li>Date Completed: {auditDateFormatted}</li>
                                    <li>Audit PDF sent by other means</li>
                                </ul>
                                :
                                <ul>
                                    <li>File Name: <a className="anchorButton" onClick={handleClickDownload(pdfDocumentId)}>{getPdfDisplayName()}</a></li>
                                    <li>Uploaded: {pdfUploadedDate}</li>
                                </ul>
                            }

                        </CardBody>
                        :
                        <div>
                        {(deferAuditPDF)
                            ?
                                <CardText>Audit PDF sent by other means</CardText>
                            :
                                <CardText>No audit file assigned</CardText>
                        }
                        </div>
                }
            </Card >

        return text;
    }

    const cardWaiverInformation = function () {
        var text = <></>;
        var title = `${settings.upload.entityType}`;
       

        if (checkboxPdf) {
            return text;
        }

        if (pdfDocumentId.length>0) {
            text =
                <Card body>
                    <CardTitle>{title} PDF</CardTitle>
                    <CardBody>
                        <ul>
                            <li>File Name: {getPdfDisplayName()}</li>
                            <li>Uploaded: {pdfUploadedDate}</li>
                        </ul>
                    </CardBody>
                </Card >
        }
        return text;
    }

    const showCheckbox = function () {
        var render = <></>;
        if (!props.showCheckbox) {
            return render;
        }
        // NOTE - The previous logic was just testing for non-null container parent, but affiliate waivers also have a non-null parent, so the correct check is by role
//        if (props.containerParentOfAudit) {
        if (props.role === enums.documentRole.audit) {
            render =
                <Label className="ml-4" check>
                    <Input type="checkbox"
                        name="uploadQuestion"
                        onChange={handleChangeCheckbox}
                        disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                    checked={deferAuditPDF ? true : false}
                    />{settings.upload.checkboxLegend}
                </Label>
        } else {
            render =
                <Label className="ml-4" check>
                    <Input type="checkbox"
                        name="uploadQuestion"
                        onChange={handleChangeCheckbox}
                        disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                        checked={(checkboxPdf ? true : false)}
                    />{settings.upload.checkboxLegend}
                </Label>
        }
        return render;
    }

    const uploader = <GenericFileUploader
                        actionType={settings.upload.actionType}
                        clickAction={handleUploadFile}
                        entityType={settings.upload.entityType}
                        entityId={""}
                        fileExtensions={".PDF"}
                        uploadLegend={settings.upload.legend}
                        icon="bi bi-arrow-bar-up"
        isEnabled={!(utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified))
            && !((adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin))}
                        uploadedFile={file}
                    />

    

    const showFileUploader = function () {
        var render = <></>;

        if (props.role === enums.documentRole.audit) {
            if (!deferAuditPDF) {
                return uploader;
            }

        } else {
            if (!checkboxPdf) {
                return uploader;
            }
        }

        return render;
    }

    const getPdfDisplayName = function (): string {
        if (fileName && fileName.length > 0) {
            return fileName;
        }
        return pdfDocumentId ? pdfDocumentId : "";
    }

    useEffect(() => {
       if (props.role !== enums.documentRole.audit) {
            return;
        }

        setHasAudit((props.containerParentOfAudit && props.containerParentOfAudit.audit) ? true : false);

        setAuditDateFormatted("");
        setAudReceivedDate("");
// problem? setDeferAuditPDF(false);
        var pdfDate = "";
        var pdfDocId = "";
        var fName = "";

        if (props.containerParentOfAudit && props.containerParentOfAudit.audit) {
            var affiliate : string | null = null;
            var ar: AffiliateReporting = props.containerParentOfAudit as AffiliateReporting;
            if (ar && ar.affiliateReference) {
                affiliate = ar.affiliateReference.code
            }
            var pdf = props.containerParentOfAudit.audit.auditPdf;
            var hasNoUpload = pdf ? utilityHelper.isNoSendDocumentRelationship(pdf) : props.containerParentOfAudit.audit.sendAuditPdf;
            setAuditDateFormatted(dateHelper.stringDateInFormat(props.containerParentOfAudit.audit.datePerformed, "LL/dd/yyyy"))
            setDeferAuditPDF(hasNoUpload);
            if (pdf) {
                if (!hasNoUpload) {
                    pdfDocId = pdf.documentId;
                }
                //setPdfDocumentId(pdfDocId);
                fName = (pdf.name && pdf.name.length > 0) ? pdf.name : "";
                if (affiliate) {
                    setAudReceivedDate("");
                    pdfDate = dateHelper.stringDateInFormat(pdf.timestamp, "LL/dd/yyyy HH:mm");
                } else {
                    pdfDate = dateHelper.stringDateInFormat(pdf.timestamp, "LL/dd/yyyy HH:mm");
                    setAudReceivedDate(pdfDate);
                    // Check to see if we already have an audit received date. Only assign if this is the first upload, after that changes can only be made by admins using the summary
                    // panel control. 
                    if (currentAfr && !currentAfr.auditReceivedDate) {
                        dispatch(setAuditReceivedDate(pdf.timestamp));
                    }
                }
            } else {
                setAudReceivedDate("");
            }
        }
        setPdfDocumentId(pdfDocId);
        setPdfUploadedDate(pdfDate);
        setFileName(fName);
    }, [props.containerParentOfAudit]);


    useEffect(function () {
        if (!currentAfr) {
            return;
        }
        if (props.role === enums.documentRole.audit) {
            return;
        }
        var documents = getArrayDocuments();
        if (!documents) {
            return;
        }
        var doc: DocumentRelationship;
        setCheckboxPdf(false);
        var pdfDocId = "";
        var pdfDate = "";
        var fName = "";
        setPdfDocumentId("");
        setPdfUploadedDate("");
        setFileName("");
        var affiliate : string | null = null;
        var ar: AffiliateReporting = props.containerParentOfAudit as AffiliateReporting;
        if (ar && ar.affiliateReference) {
            affiliate = ar.affiliateReference.code
        }
        var index = getFirstDocumentIndexFromRole(currentAfr, props.role, affiliate);
        if (index > -1) {
            doc = documents[index];
            setCheckboxPdf(true);
            var docId = doc.documentId ? doc.documentId : "";
            if (!utilityHelper.isNullDocumentRelationship(doc)) {
                setCheckboxPdf(false);
                pdfDocId = docId;
                fName = doc.name;
                pdfDate = dateHelper.stringDateInFormat(doc.timestamp, "LL/dd/yyyy HH:mm");
            }
        }
        setPdfDocumentId(pdfDocId);
        setPdfUploadedDate(pdfDate);
        setFileName(fName);

    }, [getArrayDocuments(), props.containerParentOfAudit]);


    return (
        <React.Fragment>
            <Row>
                <Col>

                        <div>
                            <Row>
                                <Col>

                                    <Table borderless>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {showFileUploader()}
                                                    <br />
                                                    {showCheckbox()}
                                                </td>

                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col>
                                {showPdfInformation()}


                                </Col>
                            </Row>
                        </div>
                </Col>
            </Row>
        </React.Fragment>
    )

}

export default UploadDocumentByRole;

UploadDocumentByRole.defaultProps = {
    role: enums.documentRole.impact_fee_waiver,
    pdfType: enums.pdfType.AFRWaivers,
    containerParentOfAudit: null,
    showCheckbox: true,
    saveAudit: null
}

